import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { NavLink } from "@/components/NavLink";
import { Home, Gamepad2, Hash, List, Pencil, Wrench, BarChart3, MessageSquare, Info, Shield, FileText, HelpCircle, Mail, BookOpen } from "lucide-react";

const Sitemap = () => {
  const sitePages = [
    {
      category: "Main Tools",
      pages: [
        { name: "Home", path: "/", description: "Random number generator and tools homepage", icon: Home },
        { name: "Games", path: "/games", description: "Coin flip and dice roller games", icon: Gamepad2 },
        { name: "Numbers", path: "/numbers", description: "Generate random numbers with custom ranges", icon: Hash },
        { name: "Lists", path: "/lists", description: "Random list picker and shuffler", icon: List },
        { name: "Drawings", path: "/drawings", description: "Random drawing and selection tools", icon: Pencil },
        { name: "Web Tools", path: "/web-tools", description: "Additional random generation utilities", icon: Wrench },
      ]
    },
    {
      category: "Information",
      pages: [
        { name: "Knowledge Center", path: "/blog", description: "Articles about probability, statistics, and random number theory", icon: BookOpen },
        { name: "Learn More", path: "/learn-more", description: "How our random number generator works", icon: Info },
        { name: "Testimonials", path: "/testimonials", description: "User reviews and feedback", icon: MessageSquare },
        { name: "Statistics", path: "/statistics", description: "Usage statistics and analytics", icon: BarChart3 },
        { name: "FAQ", path: "/faq", description: "Frequently asked questions", icon: HelpCircle },
        { name: "About Us", path: "/about", description: "About RandomGen and our mission", icon: Info },
      ]
    },
    {
      category: "Education Resources",
      pages: [
        { name: "Primary Schools", path: "/education/primary-school", description: "Random number generator tools and lesson plans for elementary education", icon: Info },
        { name: "High Schools", path: "/education/high-school", description: "Statistics and probability resources for secondary education", icon: Info },
        { name: "Universities", path: "/education/university", description: "Research-grade RNG for academic studies and scientific computing", icon: Info },
        { name: "College & Community College", path: "/education/college", description: "Statistics courses, research projects, and professional applications for college students", icon: Info },
        { name: "Homeschool Resources", path: "/education/homeschool", description: "Multi-age learning tools and activities for homeschool families and co-ops", icon: Info },
        { name: "Adult Learning & Professional Development", path: "/education/adult-learning", description: "Certification prep, workforce training, and continuing education tools", icon: Info },
      ]
    },
    {
      category: "Knowledge Center Articles",
      pages: [
        { name: "What is a Random Number Generator?", path: "/blog/what-is-random-number-generator", description: "Complete guide to RNG types, algorithms, and security", icon: BookOpen },
        { name: "Probability vs Statistics", path: "/blog/probability-vs-statistics", description: "Key differences explained with practical examples", icon: BookOpen },
        { name: "Monte Carlo Simulation Guide", path: "/blog/monte-carlo-simulation-guide", description: "Practical guide with real-world applications", icon: BookOpen },
      ]
    },
    {
      category: "Legal & Support",
      pages: [
        { name: "Privacy Policy", path: "/privacy", description: "How we handle your data", icon: Shield },
        { name: "Terms of Service", path: "/terms", description: "Terms and conditions of use", icon: FileText },
        { name: "Contact", path: "/contact", description: "Get in touch with us", icon: Mail },
      ]
    }
  ];

  return (
    <Layout>
      <SEO 
        title="Sitemap - All Random Number Generator Tools & Pages"
        description="Complete sitemap of all random number generator tools, games, and information pages. Find all features and resources in one place."
        canonical="https://numbers-generator.com/sitemap"
        keywords="sitemap, site navigation, random generator tools, all pages"
      />
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <header className="mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
            RandomGen Site Map
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Complete directory of all pages and tools available on RandomGen
          </p>
        </header>

        <div className="space-y-8">
          {sitePages.map((section) => (
            <Card key={section.category} className="p-6">
              <h2 className="text-2xl font-semibold mb-6 text-foreground flex items-center gap-2">
                {section.category}
              </h2>
              <nav className="grid gap-4 md:grid-cols-2">
                {section.pages.map((page) => {
                  const Icon = page.icon;
                  return (
                    <NavLink
                      key={page.path}
                      to={page.path}
                      className="group flex items-start gap-3 p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-accent/50 transition-all duration-200"
                    >
                      <Icon className="w-5 h-5 mt-0.5 text-primary group-hover:scale-110 transition-transform" />
                      <div>
                        <div className="font-semibold text-foreground group-hover:text-primary transition-colors">
                          {page.name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {page.description}
                        </div>
                      </div>
                    </NavLink>
                  );
                })}
              </nav>
            </Card>
          ))}
        </div>

        <footer className="mt-12 text-center text-sm text-muted-foreground">
          <p>All pages are optimized for search engines and mobile devices</p>
        </footer>
      </div>
    </Layout>
  );
};

export default Sitemap;
